import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { InstructDialog } from "@/components/app/InstructDialog";
import { celebrateInstruction } from "@/lib/celebrate";
import {
  CaretDown, CaretUp, CurrencyGbp,
} from "@phosphor-icons/react";

function formatCurrency(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);
}

export default function QuoteViewPage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSolicitor = user?.role === "solicitor";
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(true);
  const [instructDialogOpen, setInstructDialogOpen] = useState(false);
  const [clientInstructing, setClientInstructing] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [clientContact, setClientContact] = useState({ name: "", email: "", phone: "" });

  useEffect(() => {
    API.get(`/quotes/${quoteId}`).then(({ data }) => setQuote(data)).catch(() => {}).finally(() => setLoading(false));
  }, [quoteId]);

  const handlePrint = () => window.print();

  const handleClientInstruct = async () => {
    setClientInstructing(true);
    try {
      const { data } = await API.post(`/quotes/${quoteId}/client-instruct`, clientContact);
      setQuote(data);
      setShowClientForm(false);
      toast.success("Thank you! The solicitor has been notified of your instruction.");
      // Conversion moment — haptic buzz + brand confetti + scroll to the
      // "Instruction Received" badge.
      celebrateInstruction("instructed-badge");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit instruction");
    } finally {
      setClientInstructing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <p className="text-slate-500 text-sm">Quote not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quote-view-page">
      <div className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* Header logo: when this quote came from a bespoke-tool referrer
                with branding, the backend stamps `quote.brand` with their
                logo URL + colours, otherwise we fall back to the UKC logo. */}
            {quote.brand?.logo_url ? (
              <img
                src={quote.brand.logo_url}
                alt={quote.brand.company || "Referrer"}
                className="h-8 w-auto shrink-0 max-w-[160px] object-contain"
                data-testid="quote-brand-logo"
              />
            ) : (
              <img src="/logo.png" alt="UKC Legal" className="h-8 w-auto shrink-0" style={{ filter: 'brightness(0) saturate(100%)' }} />
            )}
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {quote.brand?.company ? `${quote.brand.company} · Conveyancing Quote` : "Conveyancing Quote"}
              </h1>
              <p className="text-xs text-slate-500">Generated {new Date(quote.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 print:hidden">
            <button
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${quoteId}/pdf`, "_blank", "noopener,noreferrer")}
              data-testid="download-pdf-btn"
              className="text-xs font-medium text-blue-600 hover:underline">
              Download PDF
            </button>
            <button onClick={handlePrint} data-testid="print-btn" className="text-xs font-medium text-blue-600 hover:underline">
              Print
            </button>
            {isSolicitor && !quote.instructed && !quote.client_instructed && (
              <Button onClick={() => setInstructDialogOpen(true)} data-testid="instruct-btn"
                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                Instruct
              </Button>
            )}
            {isSolicitor && quote.client_instructed && !quote.instructed && (
              <Button onClick={() => setInstructDialogOpen(true)} data-testid="instruct-btn"
                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                Open Case
              </Button>
            )}
            {!isSolicitor && !quote.instructed && !quote.client_instructed && (
              <Button onClick={() => setShowClientForm(true)} data-testid="client-instruct-btn"
                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                Instruct Us
              </Button>
            )}
            {(quote.instructed || quote.client_instructed) && (
              <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full ${
                quote.instructed ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
              }`} data-testid="instructed-badge">
                {quote.instructed ? "Case Opened" : "Instruction Received"}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Client info */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500 font-medium">Client</p>
              <p className="text-sm font-semibold text-slate-900">{quote.client_name}</p>
              {quote.client_email && <p className="text-xs text-slate-500">{quote.client_email}</p>}
              {quote.additional_clients?.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {quote.additional_clients.map((c, i) => (
                    <p key={i} className="text-xs text-slate-500">+ {c.name}{c.email ? ` (${c.email})` : ""}</p>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Property Value</p>
              <p className="text-sm font-semibold text-slate-900">{formatCurrency(quote.property_value)}</p>
              {(quote.property_address || quote.property_postcode) && (
                <p className="text-xs text-slate-500 mt-0.5">
                  {[quote.property_address, quote.property_postcode].filter(Boolean).join(", ")}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Transaction</p>
              <p className="text-sm font-medium text-slate-700">{({ buying: "Buying", selling: "Selling", both: "Buying & Selling", remortgage: "Remortgage", transfer: "Transfer of Equity", grant_of_lease: "Grant of Lease" })[quote.transaction_type] || quote.transaction_type} · <span className="capitalize">{quote.property_type}</span></p>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Details</p>
              <div className="flex gap-2 mt-0.5 flex-wrap">
                {quote.mortgage && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Mortgage</span>}
                {quote.first_time_buyer && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">First-time buyer</span>}
                {quote.second_home && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700">Second Home / BTL</span>}
                {quote.new_build && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">New Build</span>}
                {quote.shared_ownership && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700">Shared Ownership</span>}
                {quote.gifted_deposit && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">Gifted Deposit ({quote.num_giftors})</span>}
                {quote.htb_isa && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">Help to Buy ISA ({quote.num_htb_isa || 1})</span>}
                {quote.lifetime_isa && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-pink-50 text-pink-700">Lifetime ISA ({quote.num_lifetime_isa || 1})</span>}
                {quote.ltd_company && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-700 text-white">Ltd Company</span>}
                {quote.auction && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">Auction</span>}
                {!quote.mortgage && !quote.first_time_buyer && !quote.new_build && !quote.shared_ownership && !quote.gifted_deposit && !quote.htb_isa && !quote.lifetime_isa && !quote.ltd_company && !quote.auction && <span className="text-xs text-slate-400">-</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Quote breakdown */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="p-6 space-y-5">
            <div>
              <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide mb-2">Legal Fees</h3>
                <div className="space-y-1.5">
                  {(quote.legal_items || [{ name: "Solicitor's fee", amount: quote.legal_fee }])
                    .filter((item) => Number(item.amount || 0) > 0)
                    .map((item, i) => (
                    <div key={i} className="flex justify-between text-sm"><span className="text-slate-600">{item.name}</span><span className="font-medium">{formatCurrency(item.amount)}</span></div>
                  ))}
                  <div className="flex justify-between text-sm"><span className="text-slate-600">VAT (20%)</span><span className="font-medium">{formatCurrency(quote.vat_on_legal)}</span></div>
                </div>
            </div>

            <hr className="border-slate-100" />

            <div>
              <button onClick={() => setShowBreakdown(!showBreakdown)} className="flex items-center justify-between w-full mb-2">
                <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide">Disbursements</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900">{formatCurrency(quote.disbursements_total)}</span>
                  {showBreakdown ? <CaretUp className="w-3 h-3 text-slate-400" /> : <CaretDown className="w-3 h-3 text-slate-400" />}
                </div>
              </button>
              {showBreakdown && (
                <div className="space-y-1.5 pl-2 border-l-2 border-slate-100 ml-1">
                  {quote.disbursements
                    .filter((d) => Number(d.amount || 0) > 0)
                    .map((d, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-500">{d.name}</span>
                      <span className="text-slate-700">{formatCurrency(d.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {quote.sdlt && quote.sdlt.total > 0 && (
              <>
                <hr className="border-slate-100" />
                <div>
                  <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide mb-2">Stamp Duty (SDLT)</h3>
                  <div className="space-y-1.5 pl-2 border-l-2 border-slate-100 ml-1">
                    {quote.sdlt.bands.map((b, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-slate-500">{formatCurrency(b.from)} – {formatCurrency(b.to)} @ {b.rate}%</span>
                        <span className="text-slate-700">{formatCurrency(b.tax)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between text-sm font-medium mt-2">
                    <span className="text-slate-700">SDLT Total</span>
                    <span>{formatCurrency(quote.sdlt.total)}</span>
                  </div>
                  {quote.sdlt.first_time_buyer && <p className="text-[10px] text-emerald-600 font-medium mt-1">First-time buyer relief applied</p>}
                  {quote.sdlt.second_home && <p className="text-[10px] text-red-600 font-medium mt-1">5% second home / BTL surcharge applied</p>}
                  {!quote.sdlt.second_home && quote.sdlt.ltd_company && <p className="text-[10px] text-red-600 font-medium mt-1">5% higher rate (Ltd company purchase) applied</p>}
                </div>
              </>
            )}
            {quote.sdlt && quote.sdlt.total === 0 && quote.transaction_type !== "selling" && (
              <>
                <hr className="border-slate-100" />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Stamp Duty (SDLT)</span>
                  <span className="font-medium text-emerald-600">{formatCurrency(0)}</span>
                </div>
              </>
            )}
          </div>

          <div className="bg-slate-900 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide font-medium">Estimated Total</p>
                <p className="text-xs text-slate-400 mt-0.5">Including VAT, disbursements{quote.sdlt?.total > 0 ? " & SDLT" : ""}</p>
              </div>
              <p className="text-3xl font-bold" data-testid="quote-total" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(quote.total)}</p>
            </div>
          </div>
        </div>

        {quote.additional_notes && (
          <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm mb-6">
            <p className="text-xs font-semibold text-slate-900 uppercase tracking-wide mb-1">Notes</p>
            <p className="text-sm text-slate-600">{quote.additional_notes}</p>
          </div>
        )}

        <p className="text-center text-[10px] text-slate-400 mt-8">
          This quote is an estimate and may vary. Disbursements are approximate and subject to third-party charges. Quote ref: {quote.id.slice(0, 8).toUpperCase()}
        </p>
        {/* Optional referrer footer + T&Cs link, swapped in when the quote
            came from a bespoke-tool referrer with branding overrides. */}
        {(quote.brand?.footer_text || quote.brand?.terms_url) && (
          <p
            className="text-center text-[10px] text-slate-400 mt-2 max-w-xl mx-auto"
            data-testid="quote-brand-footer"
          >
            {quote.brand.footer_text || ""}
            {quote.brand.footer_text && quote.brand.terms_url ? " · " : ""}
            {quote.brand.terms_url && (
              <a
                href={quote.brand.terms_url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-slate-600"
                data-testid="quote-brand-terms-link"
              >
                Terms &amp; Conditions
              </a>
            )}
          </p>
        )}
        {/* Spacer so the sticky mobile CTA below doesn't cover the footer line */}
        <div className="sm:hidden h-20" aria-hidden />
      </div>

      {/* Sticky mobile CTA — keeps the primary Instruct CTA visible while the
          client scrolls. Hidden ≥sm. Respects iOS safe-area inset. */}
      {!quote.instructed && !quote.client_instructed && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3 print:hidden"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
          data-testid="mobile-sticky-cta"
        >
          {isSolicitor ? (
            <Button
              onClick={() => setInstructDialogOpen(true)}
              data-testid="mobile-sticky-instruct-btn"
              className="w-full h-11 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Instruct · {formatCurrency(quote.total)}
            </Button>
          ) : (
            <Button
              onClick={() => setShowClientForm(true)}
              data-testid="mobile-sticky-request-instruct-btn"
              className="w-full h-11 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Instruct Us · {formatCurrency(quote.total)}
            </Button>
          )}
        </div>
      )}

      {/* Client Instruct Dialog */}
      <Dialog open={showClientForm} onOpenChange={setShowClientForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Instruct Us</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800 font-medium">Ready to proceed?</p>
              <p className="text-xs text-emerald-600 mt-0.5">Confirm your details and the solicitor will be notified to open your case.</p>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Your Name</Label>
                <Input data-testid="client-instruct-name" value={clientContact.name}
                  onChange={(e) => setClientContact(p => ({ ...p, name: e.target.value }))}
                  placeholder={quote.client_name || "Full name"} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Email</Label>
                <Input data-testid="client-instruct-email" type="email" value={clientContact.email}
                  onChange={(e) => setClientContact(p => ({ ...p, email: e.target.value }))}
                  placeholder={quote.client_email || "email@example.com"} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Phone</Label>
                <Input data-testid="client-instruct-phone" value={clientContact.phone}
                  onChange={(e) => setClientContact(p => ({ ...p, phone: e.target.value }))}
                  placeholder={quote.client_phone || "+44..."} />
              </div>
            </div>
            <Button onClick={handleClientInstruct} disabled={clientInstructing}
              data-testid="confirm-client-instruct-btn"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
              {clientInstructing ? "Submitting..." : "Confirm Instruction"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Solicitor Instruct Dialog */}
      <InstructDialog open={instructDialogOpen} onOpenChange={setInstructDialogOpen}
        quoteId={quote.id} quoteName={quote.client_name} />
    </div>
  );
}
